import { memo, useMemo } from 'react';
import MapCluster from 'components/elements/Map/MapCluster';
import { useMapClusters } from 'hooks/projectMap/useMapClusters';
import { useClustersToDisplay } from 'state/filterMapPins';
import { useIsPolygonDrawingEnabled } from 'state/map';
import { IdentifiableCoords } from 'types/map';

type Props = {
  activeMarker?: IdentifiableCoords;
  onClusterClick: ({
    lat,
    lng,
    count,
    index,
  }: {
    lat: number;
    lng: number;
    count: number;
    index: string;
  }) => void;
};

const RenderClusters = memo(
  ({
    clusterKey,
    onClusterClick,
  }: Omit<Props, 'activeMarker'> & { clusterKey: string }) => {
    const [clusters] = useClustersToDisplay();

    if (!clusterKey) {
      return null;
    }

    return (
      <>
        {Object.values(clusters).map((cluster: any) => {
          return (
            <MapCluster
              clusterKey={`cluster-${cluster.index}`}
              lat={cluster.centroid[0]}
              lng={cluster.centroid[1]}
              label={cluster.count.toString()}
              onClick={() =>
                onClusterClick({
                  lat: cluster.centroid[0],
                  lng: cluster.centroid[1],
                  count: cluster.count,
                  index: cluster.index,
                })
              }
            />
          );
        })}
      </>
    );
  },
);

const RenderActiveCluster = memo(
  ({ clusterKey, onClusterClick, activeMarker }: Props & { clusterKey: string }) => {
    const [clusters] = useClustersToDisplay();

    const activeCluster = clusters[activeMarker?.id || ''];

    if (!clusterKey || !activeMarker || !activeCluster?.centroid) {
      return null;
    }

    const [lat, lng] = activeCluster.centroid;
    if (!lat || !lng) {
      return null;
    }

    return (
      <MapCluster
        clusterKey={`cluster-${activeCluster.index}`}
        lat={lat}
        lng={lng}
        label={activeCluster.count.toString()}
        onClick={() =>
          onClusterClick({
            lat,
            lng,
            count: activeCluster.count,
            index: activeCluster.index,
          })
        }
        isActive
      />
    );
  },
);

const ProjectClusters = ({ onClusterClick, activeMarker }: Props) => {
  useMapClusters();
  const [clustersForCurrentResolution] = useClustersToDisplay();
  const [isPolygonDrawingEnabled] = useIsPolygonDrawingEnabled();

  const clusterKey = useMemo(() => {
    /* Generate a unique key based on the current clusters to prevent
     * unnecessary re-renders.
     */
    return Object.values(clustersForCurrentResolution)
      .map((cluster) => `cluster-${cluster.index}`)
      .join('-');
  }, [clustersForCurrentResolution]);

  if (!clusterKey) {
    return null;
  }
  if (isPolygonDrawingEnabled) {
    // Disable clusters when polygon drawing is enabled
    return null;
  }

  return (
    <>
      <RenderClusters clusterKey={clusterKey} onClusterClick={onClusterClick} />;
      <RenderActiveCluster
        clusterKey={clusterKey}
        onClusterClick={onClusterClick}
        activeMarker={activeMarker}
      />
    </>
  );
};

export default memo(ProjectClusters);
